import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AFRICA_COUNTRIES } from "@/data/products";

export default function SampleRequestModal({ product, open, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    full_name: "", email: "", company: "", phone: "",
    country: "", address: "", variants: [], quantity: "Sample pack", purpose: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const toggleVariant = (v) => setForm((f) => ({ ...f, variants: f.variants.includes(v) ? f.variants.filter((x) => x !== v) : [...f.variants, v] }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/sample/request", { ...form, product: product.name });
      toast.success(data.message);
      setDone(true);
    } catch { toast.error("Please try again."); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="sample-modal">
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-background rounded-2xl border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center" data-testid="sample-modal-close">
          <X className="w-4 h-4" />
        </button>

        {done ? (
          <div className="p-10 text-center" data-testid="sample-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-gold-400 text-neutral-950 flex items-center justify-center font-black text-xl">✓</div>
            <div className="font-display font-bold text-2xl mt-4">{t("sample.success_title")}</div>
            <p className="text-muted-foreground mt-2">{t("sample.success_body")}</p>
            <button onClick={onClose} className="mt-6 bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full px-6 py-2.5 text-sm font-semibold">Close</button>
          </div>
        ) : (
          <div className="p-8">
            <div className="eyebrow mb-2">{product.name}</div>
            <h2 className="font-display font-bold text-3xl">{t("sample.title")}</h2>
            <p className="text-muted-foreground mt-2 text-sm">{t("sample.subtitle")}</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {product.variants && product.variants.length > 1 && (
                <div>
                  <Label>{t("sample.variants")}</Label>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((v) => (
                      <button type="button" key={v.name} onClick={() => toggleVariant(v.name)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.variants.includes(v.name) ? "bg-gold-400 text-neutral-950 border-gold-400" : "border-border hover:border-gold-400"}`} data-testid={`sample-variant-${v.name.toLowerCase().replace(/\s+/g,'-')}`}>
                        {v.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <F label={`${t("sample.name")} *`} value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testId="sample-name" />
                <F label={`${t("sample.email")} *`} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="sample-email" />
                <F label={t("sample.company")} value={form.company} onChange={(v) => setForm({ ...form, company: v })} testId="sample-company" />
                <F label={t("sample.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="sample-phone" />
                <div>
                  <Label>{t("sample.country")} *</Label>
                  <select required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid="sample-country">
                    <option value="">{t("common.select_country")}</option>
                    {AFRICA_COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                    <option>United Kingdom</option><option>Other</option>
                  </select>
                </div>
                <F label={t("sample.quantity")} value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} testId="sample-quantity" />
              </div>
              <div>
                <Label>{t("sample.address")} *</Label>
                <textarea required rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid="sample-address" />
              </div>
              <div>
                <Label>{t("sample.purpose")}</Label>
                <textarea rows={3} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" placeholder="e.g. Retail listing review · Hotel supply evaluation" data-testid="sample-purpose" />
              </div>
              <button disabled={loading} className="w-full bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full h-12 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="sample-submit">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} {t("sample.submit")}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const Label = ({ children }) => <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{children}</div>;
const F = ({ label, value, onChange, required, type = "text", testId }) => (
  <div>
    <Label>{label}</Label>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid={testId} />
  </div>
);
